import { useRouteModals } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { RouteLegForm } from '@lp/features/RoutePlanner/components/RouteLegForm/RouteLegForm';
import { RouteLegFormMode } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { ViewRouteSchemaModal } from './components/ViewRouteSchemaModal';
import { ViewClinicalSpecialistModal } from './components/ViewClinicalSpecialistModal';
import { RemoveResourceModal } from './components/RemoveResourceModal';
import { ViewDeviceModal } from './components/ViewDeviceModal';
import { ViewSurgeonModal } from './components/ViewSurgeonModal';
import { ViewHubModal } from './components/ViewHubModal';
import { RoutePlannerModal } from './components/RoutePlannerModal';
import { RoutePlannerDeviceList } from './components/DeviceListTable/DeviceList';
import { RoutePlannerSurgeonList } from './components/SurgeonListTable/SurgeonList';
import { RoutePlannerClinicalSpecialistList } from './components/ClinicalSpecialistListTable/ClinicalSpecialistList';
import { RoutePlannerHubsList } from './components/HubsListTable/HubList';
import { QuoteDetailsModal } from '../QuoteDetails/QuoteDetailsModal';
import { QuoteCommentModal } from '../QuoteComment/QuoteCommentModal';
import { RemoveClinicalSpecialistModal } from './components/RemoveClinicalSpecialistModal';
import { RemoveDeviceModal } from './components/RemoveDeviceModal';
import { OverrideFlowModal } from './components/OverrideFlowModal';
import { LogisticsStatusLog } from './components/LogisticStatusLog/LogisticsStatusLog';
import { ConfirmReservationModal } from './components/ConfirmReservationModal';
import { SecondaryResourceSelect } from './components/SecondaryResourceSelect/SecondaryResourceSelect';
import { RemoveConnectedLegsModal } from './components/RemoveConnectedLegsModal';
import { DeviceDeliveryInfoModal } from './components/DeviceDeliveryInfoModal';
import { QuoteRecallModal } from './components/QuoteRecallModal';
import { RandomizationResultModal } from './components/RandomizationResultModal';
import { RoutePlannerSideDrawer } from './components/RoutePlannerSideDrawer';
import { RemoveActiveGroundTripLegModal } from './components/RemoveActiveGroundTripLegModal';
import { ReplaceExistingRouteModal } from './components/ReplaceExistingRouteModal';

export const RouteModals = () => {
  const {
    data: { type, context },
    isOpen,
    isLoading,
    onCloseModal,
    setIsLoading,
  } = useRouteModals();

  const renderModal = () => {
    switch (type) {
      case RouteModalTypes.LogisticsStatusLog:
        return (
          <RoutePlannerSideDrawer
            isDismissable={false}
            isKeyboardDismissDisabled
            isOpen={isOpen}
            onClose={onCloseModal}
            isLoading={isLoading}
            title="Logistics status change log"
          >
            <LogisticsStatusLog />
          </RoutePlannerSideDrawer>
        );
      case RouteModalTypes.AddNewLeg:
        return (
          <RouteLegForm
            mode={RouteLegFormMode.Create}
            title="Add New Leg"
            size="4xl"
            isOpen={isOpen}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.EditLeg:
        return (
          <RouteLegForm
            mode={RouteLegFormMode.Edit}
            title="Edit Leg"
            size="4xl"
            isOpen={isOpen}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.AssignSpecialist:
        return (
          <RoutePlannerModal
            size="5xl"
            onClose={onCloseModal}
            isLoading={isLoading}
            isOpen={isOpen}
            title="Reserve Clinical Specialist"
            baseClassName="max-w-352!"
          >
            <RoutePlannerClinicalSpecialistList {...context} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.ViewSpecialist:
        return (
          <ViewClinicalSpecialistModal
            title="Clinical Specialist"
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.AssignDevice:
        return (
          <RoutePlannerModal
            size="5xl"
            onClose={onCloseModal}
            isLoading={isLoading}
            isOpen={isOpen}
            title="Reserve Device"
          >
            <RoutePlannerDeviceList {...context} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.ViewDevice:
        return (
          <ViewDeviceModal title="Device" isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal} {...context} />
        );
      case RouteModalTypes.AssignSurgeon:
        return (
          <RoutePlannerModal
            size="5xl"
            onClose={onCloseModal}
            isLoading={isLoading}
            isOpen={isOpen}
            title="Assign Retrieval Surgeon"
            baseClassName="max-w-352!"
          >
            <RoutePlannerSurgeonList {...context} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.ViewSurgeon:
        return (
          <ViewSurgeonModal
            title="Retrieval Surgeon"
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.RemoveResource:
        return (
          <RemoveResourceModal
            title="Remove reserved resource"
            description="Are you sure you want to remove resource?"
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.RemoveCS:
        return (
          <RemoveClinicalSpecialistModal
            title="Remove Clinical Specialist"
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.RemoveDevice:
        return (
          <RemoveDeviceModal
            title="Remove Device"
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onCloseModal}
            {...context}
          />
        );
      case RouteModalTypes.ConfirmReservation:
        return <ConfirmReservationModal isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal} {...context} />;
      case RouteModalTypes.AssignHub:
        return (
          <RoutePlannerModal title="Select Hub" size="3xl" isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal}>
            <RoutePlannerHubsList {...context} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.ViewHub:
        return <ViewHubModal title="Hub" isOpen={isOpen} onClose={onCloseModal} isLoading={isLoading} {...context} />;
      case RouteModalTypes.QuoteDetails:
        return <QuoteDetailsModal {...context} isOpen={isOpen} onCloseModal={onCloseModal} />;
      case RouteModalTypes.QuoteComment:
        return (
          <RoutePlannerModal title={context.title} size="3xl" isOpen={isOpen} onClose={onCloseModal}>
            <QuoteCommentModal {...context} onClose={onCloseModal} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.RouteSchema:
        return <ViewRouteSchemaModal title="Route Schema" isOpen={isOpen} onClose={onCloseModal} {...context} />;
      case RouteModalTypes.OverrideFlow:
        return <OverrideFlowModal isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal} {...context} />;
      case RouteModalTypes.SecondaryResource:
        return (
          <RoutePlannerModal
            isLoading={isLoading}
            size="5xl"
            onClose={onCloseModal}
            isOpen={isOpen}
            title="Reserve Resource"
            baseClassName="max-w-352!"
          >
            <SecondaryResourceSelect setParentLoading={setIsLoading} onClose={onCloseModal} />
          </RoutePlannerModal>
        );
      case RouteModalTypes.RemoveConnectedLegs:
        return <RemoveConnectedLegsModal isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal} {...context} />;
      case RouteModalTypes.DeviceDeliveryInfo:
        return <DeviceDeliveryInfoModal isOpen={isOpen} onClose={onCloseModal} {...context} />;
      case RouteModalTypes.QuoteRecall:
        return <QuoteRecallModal isOpen={isOpen} onClose={onCloseModal} onRecall={context.onRecall} />;
      case RouteModalTypes.TrialRandomizationResult:
        return <RandomizationResultModal isOpen={isOpen} onClose={onCloseModal} />;
      case RouteModalTypes.RemoveActiveGroundTripLeg:
        return (
          <RemoveActiveGroundTripLegModal isOpen={isOpen} isLoading={isLoading} onClose={onCloseModal} {...context} />
        );
      case RouteModalTypes.ReplaceExistingRoute:
        return <ReplaceExistingRouteModal isOpen={isOpen} onClose={onCloseModal} {...context} />;
      default:
        return null;
    }
  };

  return renderModal();
};
